/**
 * Module Summary Drawer
 * Slide-in panel showing detailed AI summary and insights for a selected module
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { protectedResources } from '../config/msalConfig';
import {
    Drawer,
    Space,
    Button,
    Tag,
    Typography,
    Badge,
    message,
    Spin,
    Empty
} from 'antd';
import {
    CloseOutlined,
    SendOutlined,
    LineChartOutlined,
    BulbOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    TrophyOutlined,
    AlertOutlined,
    ThunderboltFilled,
    ReloadOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { triggerManualDigest, getModuleDigest } from '../services/digestService';

const { Title, Text } = Typography;

const CompactHeader = styled.div`
    background: white;
    margin: 0;
    padding: 16px 20px;
    border-bottom: 3px solid #F16622;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .header-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .insights-icon {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #F16622 0%, #ff8c42 100%);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 20px;
        box-shadow: 0 2px 8px rgba(241, 102, 34, 0.3);
    }

    .header-content {
        flex: 1;
    }

    .ai-badge-header {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 3px 10px;
        border-radius: 12px;
        font-weight: 600;
        font-size: 10px;
        margin-bottom: 6px;
        letter-spacing: 0.5px;
        box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);

        .ai-sparkle {
            font-size: 11px;
            animation: sparkle 2s ease-in-out infinite;
        }

        @keyframes sparkle {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
        }
    }

    .module-title {
        color: #262626;
        font-size: 24px;
        font-weight: 600;
        margin: 0;
    }
`;

const CompactScoreSection = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: ${props => props.color ? `${props.color}12` : '#fafafa'};
    border: 1px solid ${props => props.color ? `${props.color}30` : '#f0f0f0'};
    border-radius: 8px;
    margin-bottom: 12px;

    .score-badge {
        width: 70px;
        height: 70px;
        border-radius: 50%;
        background: ${props => props.color};
        color: white;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-size: 32px;
        font-weight: 700;
        flex-shrink: 0;
        box-shadow: 0 3px 8px ${props => props.color}40;

        .label {
            font-size: 11px;
            font-weight: 500;
            opacity: 0.9;
            margin-top: 2px;
        }
    }

    .score-info {
        flex: 1;

        .status-tag {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 600;
            background: ${props => props.color}30;
            color: ${props => props.color};
            margin-bottom: 6px;
            border: 1px solid ${props => props.color}50;
        }

        .description {
            font-size: 14px;
            color: #595959;
            line-height: 1.4;
        }
    }
`;

const CompactMetric = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    background: white;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    margin-bottom: 8px;
    font-size: 14px;

    .metric-label {
        color: #595959;
        font-weight: 500;
        font-size: 14px;
    }

    .metric-value {
        font-weight: 600;
        color: #262626;
        font-size: 18px;
    }
`;

const CompactInsight = styled.div`
    display: flex;
    gap: 12px;
    padding: 12px 14px;
    background: ${props => props.bg || '#fafafa'};
    border-left: 3px solid ${props => props.color || '#1890ff'};
    border-radius: 4px;
    margin-bottom: 10px;
    font-size: 15px;

    .insight-icon {
        color: ${props => props.color || '#1890ff'};
        font-size: 20px;
        margin-top: 2px;
    }

    .insight-content {
        flex: 1;

        .insight-title {
            font-weight: 600;
            color: #262626;
            margin-bottom: 4px;
            font-size: 15px;
        }

        .insight-desc {
            color: #595959;
            line-height: 1.5;
            font-size: 15px;
        }
    }
`;

export default function ModuleSummaryDrawer({ visible, module, onClose }) {
    const { instance, accounts } = useMsal();
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [digest, setDigest] = useState(null);
    const [digestNotFound, setDigestNotFound] = useState(false);

    // Fetch digest when drawer opens
    useEffect(() => {
        if (visible && module) {
            fetchDigest();
        }
    }, [visible, module]);

    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('bic:module-summary-visibility', {
                detail: { open: visible }
            })
        );
        return () => {
            window.dispatchEvent(
                new CustomEvent('bic:module-summary-visibility', {
                    detail: { open: false }
                })
            );
        };
    }, [visible]);

    // No need for global functions - iframe will execute script tags naturally

    const fetchDigest = async () => {
        if (!module) return;

        try {
            setLoading(true);
            setDigestNotFound(false);

            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const result = await getModuleDigest(module.moduleId, response.accessToken);

            if (result.notFound) {
                setDigestNotFound(true);
                setDigest(null);
            } else if (result.success) {
                setDigest(result.data);
                setDigestNotFound(false);
            }
        } catch (error) {
            console.error('Error fetching digest:', error);
            setDigestNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const getHealthColor = (score) => {
        if (score >= 80) return '#52c41a';
        if (score >= 60) return '#1890ff';
        if (score >= 40) return '#faad14';
        return '#ff4d4f';
    };

    const getHealthStatus = (score) => {
        if (score >= 80) return { text: 'Excellent', icon: <TrophyOutlined /> };
        if (score >= 60) return { text: 'Good', icon: <CheckCircleOutlined /> };
        if (score >= 40) return { text: 'Warning', icon: <WarningOutlined /> };
        return { text: 'Critical', icon: <AlertOutlined /> };
    };

    const handleGenerateDigest = async () => {
        if (!module) return;

        try {
            setSending(true);
            const response = await instance.acquireTokenSilent({
                scopes: protectedResources.api.scopes.read,
                account: accounts[0]
            });

            const email = accounts[0]?.username;
            await triggerManualDigest(module.moduleId, email, response.accessToken);

            message.success({
                content: `Digest for ${module.moduleName} queued! Refreshing digest in a few seconds...`,
                duration: 4
            });

            // Wait a bit for digest to be generated, then refetch
            setTimeout(() => {
                fetchDigest();
            }, 5000);
        } catch (error) {
            console.error('Error generating digest:', error);
            message.error('Failed to generate digest');
        } finally {
            setSending(false);
        }
    };

    if (!module) return null;

    const color = getHealthColor(module.healthScore);
    const status = getHealthStatus(module.healthScore);

    // Extract real data from digest
    const metrics = digest ? [
        { label: 'Reports Analyzed', value: digest.reportCount || 0 },
        { label: 'Pages Analyzed', value: digest.pageCount || 0 },
        { label: 'Health Score', value: `${digest.healthScore || 0}/100` },
        { label: 'Generated', value: digest.generatedAt ? new Date(digest.generatedAt).toLocaleDateString() : 'N/A' }
    ] : [];

    const insights = digest?.summary?.trends?.map((trend, idx) => ({
        type: idx % 3 === 0 ? 'success' : idx % 3 === 1 ? 'warning' : 'info',
        icon: idx % 3 === 0 ? <CheckCircleOutlined /> : idx % 3 === 1 ? <WarningOutlined /> : <BulbOutlined />,
        title: trend.title || trend.category || `Trend ${idx + 1}`,
        description: trend.description || trend.summary || trend
    })) || [];

    const recommendations = digest?.summary?.recommendations || [];

    return (
        <Drawer
            title={null}
            placement="right"
            width={550}
            onClose={onClose}
            open={visible}
            closable={false}
            styles={{
                body: { padding: 0 },
                header: { display: 'none' }
            }}
        >
            {/* Enhanced Header with Branding */}
            <CompactHeader>
                <div className="header-left">
                    <div className="insights-icon">
                        <ThunderboltFilled />
                    </div>
                    <div className="header-content">
                        <div className="ai-badge-header">
                            <span className="ai-sparkle">✨</span>
                            AI Powered by BI Collab
                        </div>
                        <h3 className="module-title">
                            {module.moduleName}
                        </h3>
                    </div>
                </div>
                <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                    style={{ color: '#8c8c8c', fontSize: '16px' }}
                />
            </CompactHeader>

            <div style={{ padding: '20px 20px 20px 20px' }}>
            {/* Compact Health Score */}
            <CompactScoreSection color={color}>
                <div className="score-badge">
                    <div>{module.healthScore}</div>
                    <div className="label">HEALTH</div>
                </div>
                <div className="score-info">
                    <span className="status-tag">{status.text}</span>
                    <div className="description">
                        Module performance and data quality score based on AI analysis
                        {module.lastUpdated && ` • Updated ${new Date(module.lastUpdated).toLocaleDateString()}`}
                    </div>
                </div>
            </CompactScoreSection>

            {/* Module Summary - Always show from module data */}
            {(() => {
                let parsedModuleSummary = null;
                try {
                    if (typeof module.summary === 'string') {
                        parsedModuleSummary = JSON.parse(module.summary);
                    } else {
                        parsedModuleSummary = module.summary;
                    }
                } catch (e) {
                    // If parsing fails, keep as null
                }

                // Check if HTML template was generated
                const isHtmlTemplate = parsedModuleSummary?.isHtml === true && parsedModuleSummary?.htmlContent;

                return parsedModuleSummary?.executiveSummary || isHtmlTemplate ? (
                    <>
                        <div style={{ marginBottom: 16, padding: '14px', background: '#e6f7ff', borderRadius: '8px', borderLeft: '4px solid #1890ff' }}>
                            <Text strong style={{ fontSize: '15px', color: '#1890ff', display: 'block', marginBottom: 8 }}>
                                <BulbOutlined style={{ marginRight: '6px' }} />
                                Executive Summary
                            </Text>
                            {isHtmlTemplate ? (
                                <iframe
                                    srcDoc={parsedModuleSummary.htmlContent}
                                    style={{
                                        width: '100%',
                                        height: '600px',
                                        border: 'none',
                                        display: 'block'
                                    }}
                                    title="Module Digest"
                                />
                            ) : (
                                <div style={{ fontSize: '14px', color: '#595959', lineHeight: '1.6' }}>
                                    {parsedModuleSummary.executiveSummary}
                                </div>
                            )}
                        </div>

                        {parsedModuleSummary.keyFindings && parsedModuleSummary.keyFindings.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 10 }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                                    Key Findings
                                </Text>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                                    {parsedModuleSummary.keyFindings.slice(0, 5).map((finding, idx) => (
                                        <li key={idx} style={{ marginBottom: '8px', color: '#595959', lineHeight: '1.5' }}>
                                            {typeof finding === 'string' ? finding : finding.description || finding.finding || JSON.stringify(finding)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {parsedModuleSummary.recommendations && parsedModuleSummary.recommendations.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 10 }}>
                                    <BulbOutlined style={{ color: '#faad14', marginRight: 6 }} />
                                    Considerations
                                </Text>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                                    {parsedModuleSummary.recommendations.slice(0, 5).map((rec, idx) => (
                                        <li key={idx} style={{ marginBottom: '8px', color: '#595959', lineHeight: '1.5' }}>
                                            {typeof rec === 'string' ? rec : rec.action || rec.recommendation || JSON.stringify(rec)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                ) : null;
            })()}

            {/* Data Volume Information */}
            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 10 }}>
                    <LineChartOutlined style={{ color: '#F16622', marginRight: 6 }} />
                    Data Volume
                </Text>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <CompactMetric>
                        <span className="metric-label">Total Reports</span>
                        <span className="metric-value">{module.dataVolume?.totalReports || 0}</span>
                    </CompactMetric>
                    <CompactMetric>
                        <span className="metric-label">Total Pages</span>
                        <span className="metric-value">{module.dataVolume?.totalPages || 0}</span>
                    </CompactMetric>
                    <CompactMetric>
                        <span className="metric-label">Analyzed Reports</span>
                        <span className="metric-value" style={{ color: '#52c41a' }}>{module.dataVolume?.analyzedReports || 0}</span>
                    </CompactMetric>
                    <CompactMetric>
                        <span className="metric-label">Analyzed Pages</span>
                        <span className="metric-value" style={{ color: '#52c41a' }}>{module.dataVolume?.analyzedPages || 0}</span>
                    </CompactMetric>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16, color: '#8c8c8c' }}>Loading additional digest data...</div>
                </div>
            ) : digestNotFound ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Empty
                        description="No digest found for this module"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={sending}
                        onClick={handleGenerateDigest}
                        style={{
                            background: '#F16622',
                            borderColor: '#F16622',
                            marginTop: 16,
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 500
                        }}
                    >
                        Generate Digest Now
                    </Button>
                </div>
            ) : (
                <>
                    {/* Refresh Button */}
                    <Space style={{ width: '100%', marginBottom: 16 }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchDigest}
                            size="small"
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={sending}
                            onClick={handleGenerateDigest}
                            size="small"
                            style={{
                                background: '#F16622',
                                borderColor: '#F16622'
                            }}
                        >
                            Generate Fresh Digest
                        </Button>
                    </Space>

                    <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 10 }}>
                        <LineChartOutlined style={{ color: '#F16622', marginRight: 6 }} />
                        Key Metrics
                    </Text>

                    {/* Compact Metrics */}
                    <div style={{ marginBottom: 16 }}>
                        {metrics.map((metric, index) => (
                    <CompactMetric key={index}>
                        <span className="metric-label">{metric.label}</span>
                        <Space size={8}>
                            <span className="metric-value">{metric.value}</span>
                            {metric.trend === 'up' && (
                                <Tag color="success" style={{ fontSize: '10px', margin: 0, padding: '0 6px' }}>
                                    {metric.change}
                                </Tag>
                            )}
                            {metric.trend === 'stable' && (
                                <Tag color="blue" style={{ fontSize: '10px', margin: 0, padding: '0 6px' }}>
                                    Stable
                                </Tag>
                            )}
                        </Space>
                    </CompactMetric>
                ))}
            </div>

            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 10 }}>
                <ThunderboltFilled style={{ color: '#F16622', marginRight: 6 }} />
                AI Insights
            </Text>

            {/* Compact Insights */}
            <div style={{ marginBottom: 16 }}>
                {insights.length > 0 ? insights.map((insight, index) => {
                    const insightColors = {
                        success: '#52c41a',
                        warning: '#faad14',
                        info: '#1890ff',
                        error: '#ff4d4f'
                    };

                    return (
                        <CompactInsight
                            key={index}
                            color={insightColors[insight.type]}
                            bg={`${insightColors[insight.type]}08`}
                        >
                            <div className="insight-icon">{insight.icon}</div>
                            <div className="insight-content">
                                <div className="insight-title">{insight.title}</div>
                                <div className="insight-desc">{insight.description}</div>
                            </div>
                        </CompactInsight>
                    );
                }) : (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#8c8c8c', fontSize: '14px' }}>
                        No insights available yet
                    </div>
                )}
            </div>

            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 10 }}>
                <BulbOutlined style={{ color: '#F16622', marginRight: 6 }} />
                Considerations
            </Text>

            {/* Compact Considerations */}
            <div style={{ marginBottom: 16 }}>
                {recommendations.length > 0 ? recommendations.map((item, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        gap: 10,
                        padding: '8px 0',
                        fontSize: '14px',
                        color: '#595959'
                    }}>
                        <Badge
                            count={index + 1}
                            style={{
                                backgroundColor: '#F16622',
                                fontSize: '12px',
                                minWidth: '22px',
                                height: '22px',
                                lineHeight: '22px'
                            }}
                        />
                        <span>{typeof item === 'string' ? item : item.text || item.description || 'Consideration'}</span>
                    </div>
                )) : (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#8c8c8c', fontSize: '14px' }}>
                        No considerations available yet
                    </div>
                )}
            </div>

            {/* Compact Footer */}
            <div style={{
                marginTop: 16,
                padding: '10px 12px',
                background: '#f0f9ff',
                border: '1px solid #d1e9ff',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#595959'
            }}>
                <ThunderboltFilled style={{ color: '#1890ff', marginRight: 6 }} />
                AI insights auto-updated with each digest generation
            </div>
                </>
            )}
            </div>
        </Drawer>
    );
}
